import { useCallback, useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MapPin, Crosshair, Trash, Warning, DeviceMobile, Globe, Camera, CameraSlash } from "@phosphor-icons/react";
import { getPosition } from "../lib/deviceInfo";
import { API } from "../lib/api";

export const selfieUrl = (attId, half) => `${API}/attendance/${attId}/selfie/${half}`;

/* Compact per-mark location / IP / device readout for the admin attendance table. */
export function GeoCell({ geo, selfieUrl, hasSelfie }) {
  if (!geo) return null;
  const hasLoc = geo.lat != null && geo.lng != null;
  let badge = { cls: "bg-zinc-100 text-zinc-500", text: "No location" };
  if (hasLoc && geo.outside_office === true) badge = { cls: "bg-red-600 text-white", text: `Outside office · ${geo.distance_m} m` };
  else if (hasLoc && geo.outside_office === false) badge = { cls: "bg-emerald-100 text-emerald-800", text: `In office · ${geo.distance_m} m` };
  else if (hasLoc) badge = { cls: "bg-blue-100 text-blue-800", text: "Located" };
  return (
    <div className="mt-1 space-y-0.5" data-testid="geo-cell">
      <div className="flex items-start gap-2">
        {hasSelfie ? (
          <a href={selfieUrl} target="_blank" rel="noreferrer" title="Open check-in selfie" data-testid="geo-selfie">
            <img src={selfieUrl} alt="Check-in selfie" className="w-10 h-10 object-cover border border-zinc-300" />
          </a>
        ) : (
          <span className="label-overline px-1.5 py-0.5 inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200"
                title={geo.selfie_error || ""} data-testid="geo-no-selfie">
            <CameraSlash size={10} weight="bold" /> No selfie
          </span>
        )}
        <span className={`label-overline px-1.5 py-0.5 inline-flex items-center gap-1 ${badge.cls}`} data-testid="geo-badge">
          <MapPin size={10} weight="bold" /> {badge.text}
        </span>
      </div>
      {hasLoc && (
        <a href={`https://www.google.com/maps?q=${geo.lat},${geo.lng}`} target="_blank" rel="noreferrer"
           className="block font-mono text-[10px] text-blue-700 hover:underline" data-testid="geo-map-link">
          {geo.lat.toFixed(5)}, {geo.lng.toFixed(5)}{geo.accuracy_m != null ? ` (±${geo.accuracy_m} m)` : ""}
        </a>
      )}
      {!hasLoc && geo.location_error && (
        <div className="font-mono text-[10px] text-zinc-500">{geo.location_error}</div>
      )}
      <div className="font-mono text-[10px] text-zinc-600 flex items-center gap-1" title={geo.user_agent || ""}>
        <Globe size={10} /> {geo.ip || "—"}
        <span className="text-zinc-300">|</span>
        <DeviceMobile size={10} /> {geo.device_id ? geo.device_id.slice(0, 8) : "—"}{geo.platform ? ` · ${geo.platform}` : ""}
      </div>
    </div>
  );
}

/* Admin-only panel to set / clear the studio's office GPS pin + radius. */
export function OfficeLocationPanel() {
  const [office, setOffice] = useState(null);
  const [form, setForm] = useState({ lat: "", lng: "", radius_m: 150, label: "" });
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/attendance/office");
      if (data && data.lat != null) {
        setOffice(data);
        setForm({ lat: data.lat, lng: data.lng, radius_m: data.radius_m, label: data.label || "" });
      } else setOffice(null);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const useMyLocation = async () => {
    setLocating(true);
    const pos = await getPosition();
    setLocating(false);
    if (pos.error) { toast.error(`Location failed: ${pos.error}`); return; }
    setForm((f) => ({ ...f, lat: Number(pos.lat.toFixed(6)), lng: Number(pos.lng.toFixed(6)) }));
    toast.success(`Pinned current location (±${Math.round(pos.accuracy)} m)`);
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.lat === "" || form.lng === "") { toast.error("Latitude and longitude are required"); return; }
    setSaving(true);
    try {
      await http.put("/attendance/office", {
        lat: Number(form.lat), lng: Number(form.lng), radius_m: Number(form.radius_m) || 150, label: form.label,
      });
      toast.success("Office location saved");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    try {
      await http.delete("/attendance/office");
      toast.success("Office location cleared");
      setForm({ lat: "", lng: "", radius_m: 150, label: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const inputCls = "rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0";

  return (
    <div className="max-w-2xl" data-testid="office-location-panel">
      <div className="text-xs text-zinc-600 font-mono mb-4 flex items-start gap-1.5">
        <Warning size={12} weight="bold" className="mt-0.5 shrink-0" />
        <span>Staff are never blocked from marking attendance. When an office pin is set, every mark is compared
          against it and shown as <strong>In office</strong> or <strong className="text-red-600">Outside office</strong> in the summary table and reports.</span>
      </div>

      <div className="border border-zinc-300 bg-white p-4 mb-4" data-testid="office-current">
        <div className="label-overline text-zinc-500">Current office pin</div>
        {office ? (
          <div className="mt-1 text-sm">
            <div className="font-semibold">{office.label || "Studio Office"}</div>
            <a href={`https://www.google.com/maps?q=${office.lat},${office.lng}`} target="_blank" rel="noreferrer"
               className="font-mono text-xs text-blue-700 hover:underline" data-testid="office-map-link">
              {office.lat}, {office.lng}
            </a>
            <div className="font-mono text-xs text-zinc-500 mt-0.5">Radius {office.radius_m} m · set by {office.updated_by}</div>
          </div>
        ) : (
          <div className="mt-1 text-sm text-zinc-500">Not set — marks are recorded with location but not flagged.</div>
        )}
      </div>

      <form onSubmit={save} className="border border-zinc-300 bg-white p-4 space-y-3" data-testid="office-form">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="label-overline block mb-1 text-zinc-700">Latitude *</label>
            <Input type="number" step="any" value={form.lat} data-testid="office-lat"
                   onChange={(e) => setForm({ ...form, lat: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="label-overline block mb-1 text-zinc-700">Longitude *</label>
            <Input type="number" step="any" value={form.lng} data-testid="office-lng"
                   onChange={(e) => setForm({ ...form, lng: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="label-overline block mb-1 text-zinc-700">Radius (m)</label>
            <Input type="number" min={20} max={5000} value={form.radius_m} data-testid="office-radius"
                   onChange={(e) => setForm({ ...form, radius_m: e.target.value })} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="label-overline block mb-1 text-zinc-700">Label</label>
          <Input value={form.label} placeholder="e.g. Studio Office, Sikar" data-testid="office-label"
                 onChange={(e) => setForm({ ...form, label: e.target.value })} className={inputCls} />
        </div>
        <div className="flex flex-wrap gap-2 pt-3 border-t border-zinc-300">
          <Button type="button" variant="outline" onClick={useMyLocation} disabled={locating} data-testid="office-use-my-location"
                  className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
            <Crosshair size={12} weight="bold" className="mr-1.5" /> {locating ? "Locating…" : "Use my current location"}
          </Button>
          {office && (
            <Button type="button" variant="outline" onClick={clear} data-testid="office-clear"
                    className="rounded-none border-red-300 text-red-700 font-mono uppercase tracking-widest text-xs hover:bg-red-50">
              <Trash size={12} weight="bold" className="mr-1.5" /> Clear
            </Button>
          )}
          <Button type="submit" disabled={saving} data-testid="office-save"
                  className="rounded-none bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs ml-auto">
            <MapPin size={12} weight="bold" className="mr-1.5" /> {saving ? "Saving…" : "Save Office Pin"}
          </Button>
        </div>
      </form>
    </div>
  );
}
